import {
	Alert,
	SpaceBetween,
} from '@cloudscape-design/components'
import Wizard, { WizardProps } from '@cloudscape-design/components/wizard'
import { useOktaAuth } from '@okta/okta-react'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { useNewApplicationFormStore } from '../../../stores/applications/newApplicationFormStore'
import { useGenericTagsManagementFormStore } from '../../../stores/tags/genericTagsManagementFormStore'
import useCreateNewApplication from '../../../hooks/mutate/useCreateNewApplication'
import usePlatforms from '../../../hooks/query/usePlatforms'
import useApplications from '../../../hooks/query/useApplications'
import useApprovedGcpProjectsWithTags from '../../../hooks/query/useApprovedGcpProjectsWithTags'
import { v4 as uuidv4 } from 'uuid'
import useCreateNewTags from '../../../hooks/mutate/useCreateNewTags'
import GkeOrNonGke from '../../../components/gcpApplicationOnboardingCreation/Steps/00-gke-or-non-gke'
import ApplicationDetails from '../../../components/gcpApplicationOnboardingCreation/Steps/01-application-details'
import ApplicationTags from '../../../components/gcpApplicationOnboardingCreation/Steps/02-tags'
import ApplicationReview from '../../../components/gcpApplicationOnboardingCreation/Steps/03-review'
import {
	getApplicationSubmitTargetFields,
	prepareApplicationSubmitPayloads,
	submitApplicationPayloads
} from '../../../helper/applicationPayloadBuilder'
import { resolveApplicationTarget } from '../../../helper/applicationTargetResolver'
import {
	applicationMatchesDuplicateTarget,
	buildDuplicateTargetFromResolution,
	getExistingEphemeralNumbersForTarget,
	getRandomTwoDigitSuffix
} from '../../../helper/applicationDuplicateTarget'

const NewApplicationForm: FC = () => {
	//Wizard
	const i18nStrings: WizardProps.I18nStrings = {
		stepNumberLabel: (stepNumber: number) => `Step ${stepNumber}`,
		collapsedStepsLabel: (stepNumber: number, stepsCount: number) =>
			`Step ${stepNumber} of ${stepsCount}`,
		skipToButtonLabel: (step: { title: string }, _stepNumber: number) =>
			`Skip to ${step.title}`,
		navigationAriaLabel: 'Steps',
		cancelButton: 'Cancel',
		previousButton: 'Previous',
		nextButton: 'Next',
		submitButton: 'Submit',
		optional: 'optional'
	}

	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string
	const navigate = useNavigate()

	// --- Data Hooks ---
	const { 
		data: platforms,
		isLoading: isPlatformsLoading
	} = usePlatforms()
	const {
		data: applications,
		isLoading: isApplicationsLoading,
		isError: isApplicationsError
	} = useApplications()
	const { 
		data: gcpProjectsList,
		isLoading: isGcpProjectsLoading,
	 } = useApprovedGcpProjectsWithTags()



	const { mutate: createNewApplication, isLoading } = useCreateNewApplication()
	const { mutate: createTags, isLoading: isCreateTagsLoading } =
		useCreateNewTags()
	const [showForm, setShowForm] = useState(false)
	const [submitValue, setSubmitValue] = useState(false)
	const [, setAdditionalEnvName] = useState('')
	const [isSitEnvironmentChecked] = useState(false)
	const [submitErrorMessage, setSubmitErrorMessage] = useState('')
	const stepValidators = useRef<Record<number, () => Promise<boolean>>>({})

	const registerStepValidation = useCallback(
		(stepIndex: number, validate: () => Promise<boolean>) => {
			stepValidators.current[stepIndex] = validate
		},
		[]
	)

	// Handlers for the footer question
	const handleNoClick = () => {
		setShowForm(true)
		setCurrentStep(0)
	}

	const handleYesClick = () => {
		navigate('/cluster')
	}

	const {
		formValues,
		currentStep,
		actions: { setFormValues, clearFormValues, setCurrentStep }
	} = useNewApplicationFormStore()

	const isNonGke = Boolean(formValues.isNonGke)
	const selectedProjectID =
		formValues.gcpProjectID?.value || formValues.gcpProjectID
	const selectedProject = useMemo(
		() =>
			gcpProjectsList?.find(
				(project: any) => project.gcpProjectID === selectedProjectID
			),
		[gcpProjectsList, selectedProjectID]
	)
	const applicationTarget = useMemo(
		() => resolveApplicationTarget(selectedProject, platforms ?? []),
		[platforms, selectedProject]
	)
	const duplicateTarget = useMemo(
		() =>
			buildDuplicateTargetFromResolution(
				applicationTarget,
				formValues.infrastructureType
			),
		[applicationTarget, formValues.infrastructureType]
	)

	const {
		formValues: tagFormValues,
		isFormValid: isTagsFormValid,
		actions: {
			clearFormValues: clearTagFormValues
		}
	} = useGenericTagsManagementFormStore()

	const {
		trigger,
		setValue,
	} = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: formValues
	})

	const [isEphemeralActive, setIsEphemeralActive] = useState(false)

	const getRandomNumber = () => {
		return getRandomTwoDigitSuffix()
	}

	const getExistingEphNumbers = useCallback(
		(appName: string) => {
			return getExistingEphemeralNumbersForTarget(
				applications,
				appName,
				duplicateTarget
			)
		},
		[applications, duplicateTarget]
	)

	const checkEphemeral = useCallback(
		(applicationName: string) => {
			if (applications) {
				return applications.some((app) => {
					if (!app || !app.details) return false
					if (app.details.applicationName === applicationName) {
						if (
							app.details.environment.value === formValues.environment.value &&
							applicationMatchesDuplicateTarget(app, duplicateTarget)
						) {
							if (app.status === 'APPROVED') {
								setValue('costCenter', app.details.costCenter || '')
								setFormValues('costCenter', app.details.costCenter || '')
								setValue('wbsCode', app.details.wbsCode || '')
								setFormValues('wbsCode', app.details.wbsCode || '')
								setValue('applicationAlias', app.details.applicationAlias || '')
								setFormValues(
									'applicationAlias',
									app.details.applicationAlias || ''
								)

								const viewers = Array.isArray(app.details.applicationViewer)
									? app.details.applicationViewer.join(',')
									: ''
								setValue('applicationViewer', viewers)
								setFormValues('applicationViewer', viewers)
								return true
							} else if (app.status === 'REJECTED') {
								setSubmitValue(false)
							} else {
								setSubmitValue(true)
							}
						}
					}
					return false
				})
			}
			return false
		},
		[
			applications,
			duplicateTarget,
			formValues.environment.value,
			setFormValues,
			setSubmitValue,
			setValue
		]
	)

	// --- Ephemeral Logic ---
	useEffect(() => {
		setSubmitValue(false)
		const isEphemeral = checkEphemeral(formValues.applicationName)
		if (isEphemeral) {
			setValue('sourceEnvName', formValues.environment.value)
			setFormValues('sourceEnvName', formValues.environment.value)

			let existingEphemeralNumbers = getExistingEphNumbers(
				formValues.applicationName
			)
			let randomNumber = getRandomNumber()

			while (existingEphemeralNumbers.includes(randomNumber)) {
				randomNumber = getRandomNumber()
			}

			const newEnvName = formValues.environment.value + randomNumber
			setAdditionalEnvName(newEnvName)
			setValue('additionalEnvName', newEnvName)
			setFormValues('additionalEnvName', newEnvName)
			setSubmitValue(false)
		}
		setIsEphemeralActive(isEphemeral)
	}, [
		formValues.environment.value,
		duplicateTarget,
		formValues.applicationName,
		applications,
		isNonGke,
		checkEphemeral,
		getExistingEphNumbers,
		setFormValues,
		setValue
	])

	useEffect(() => {
		if (isEphemeralActive) {
			let existingEphemeralNumbers = getExistingEphNumbers(
				formValues.applicationName
			)
			let randomNumber = getRandomNumber()
			while (existingEphemeralNumbers.includes(randomNumber)) {
				randomNumber = getRandomNumber()
			}
			const newEnvName = formValues.environment.value + randomNumber
			setAdditionalEnvName(newEnvName)
			setValue('additionalEnvName', newEnvName)
			setFormValues('additionalEnvName', newEnvName)
		} else {
			setValue('additionalEnvName', '')
			setFormValues('additionalEnvName', '')
		}
	}, [
		isEphemeralActive,
		formValues.applicationName,
		formValues.environment.value,
		getExistingEphNumbers,
		setFormValues,
		setValue
	])

	// function checkPlatformValues(platformName: any, env: any) {
	// 	if (isNonGke) return ''

	// 	let updatedID = ''
	// 	if (platforms) {
	// 		const filteredPlatforms = platforms.filter(
	// 			(platform) =>
	// 				platform.status === 'APPROVED' &&
	// 				platform.platformName === platformName.label
	// 		)
	// 		filteredPlatforms.map((platform) => {
	// 			if (platform.environment === env) {
	// 				setFormValues('platformName', {
	// 					label: platformName.label,
	// 					value: platform.platformID
	// 				})
	// 				setValue('platformName', {
	// 					label: platformName.label,
	// 					value: platform.platformID
	// 				})
	// 				updatedID = platform.platformID
	// 			}
	// 		})
	// 		return updatedID
	// 	}
	// 	return updatedID
	// }

	// // --- AUTO-SET PLATFORM NAME for Non-GKE ---
	// useEffect(() => {
	// 	const fetchCoreIdentityUnconditionally = async () => {
	// 		// PATH B: Non-GKE (Standalone GCP Project)
	// 		if (isNonGke && !hasExistingPlatform && formValues.gcpProjectID) {
	// 			if (selectedProject) {
	// 				let derivedName =
	// 					selectedProject.gcpProjectName ||
	// 					selectedProject.details?.gcpProjectName ||
	// 					selectedProject.details?.projectName ||
	// 					selectedProjectID
	// 				const nameParts = derivedName ? derivedName.split('-') : []
	// 				let derivedAlias =
	// 					nameParts.length >= 3
	// 						? nameParts[2].toLowerCase()
	// 						: derivedName
	// 						? derivedName.substring(0, 5).toLowerCase()
	// 						: 'custom'
	// 				let derivedOwner = 'N/A'

	// 				if (selectedProject.tagsID) {
	// 					try {
	// 						const response = await api.tags.getTagsById(
	// 							selectedProject.tagsID
	// 						)
	// 						const tag = response.body[0] as TagsDetails
	// 						if (tag && tag.details) {
	// 							derivedName = tag.details.platformName || derivedName
	// 							// Cast to any to bypass strict TS checking for legacy 'platformAlias'
	// 							derivedAlias =
	// 								tag.details.systemAlias ||
	// 								(tag.details as any).platformAlias ||
	// 								derivedAlias
	// 							derivedOwner = tag.details.platformOwner || 'N/A'
	// 						}
	// 					} catch (e) {
	// 						console.error('Failed to unconditionally fetch core tags', e)
	// 					}
	// 				}

	// 				// Lock in the core identity for the Main Form
	// 				setValue('platformName', derivedName)
	// 				setFormValues('platformName', derivedName)
	// 				setValue('platformAlias', derivedAlias)
	// 				setFormValues('platformAlias', derivedAlias)

	// 				// Lock in the core identity for the Tags Form
	// 				setTagFormValues('platformName', derivedName)
	// 				setTagFormValues('systemAlias', derivedAlias)
	// 				setTagFormValues('platformOwner', derivedOwner)
	// 			}
	// 		}
	// 		// PATH A: GKE / Existing Platform
	// 		else if (
	// 			(!isNonGke || (isNonGke && hasExistingPlatform)) &&
	// 			formValues.platformName
	// 		) {
	// 			const selectedPlatformID =
	// 				formValues.platformName?.value || formValues.platformName
	// 			// FIX: Added 'any' typing here to allow accessing untyped legacy properties
	// 			const selectedPlatform: any = platforms?.find(
	// 				(p) => p.platformID === selectedPlatformID
	// 			)

	// 			if (selectedPlatform) {
	// 				let derivedName = selectedPlatform.platformName
	// 				let derivedAlias =
	// 					selectedPlatform.systemAlias ||
	// 					selectedPlatform.platformAlias ||
	// 					'custom'
	// 				let derivedOwner = selectedPlatform.platformOwner || 'N/A'

	// 				if (selectedPlatform.tagsID) {
	// 					try {
	// 						const response = await api.tags.getTagsById(
	// 							selectedPlatform.tagsID
	// 						)
	// 						const tag = response.body[0] as TagsDetails
	// 						if (tag && tag.details) {
	// 							derivedName = tag.details.platformName || derivedName
	// 							// FIX: Cast to any here as well
	// 							derivedAlias =
	// 								tag.details.systemAlias ||
	// 								(tag.details as any).platformAlias ||
	// 								derivedAlias
	// 							derivedOwner = tag.details.platformOwner || derivedOwner
	// 						}
	// 					} catch (e) {
	// 						console.error('Failed to unconditionally fetch core tags', e)
	// 					}
	// 				}

	// 				// Lock in the core identity for the Tags Form
	// 				setTagFormValues('platformName', derivedName)
	// 				setTagFormValues('systemAlias', derivedAlias)
	// 				setTagFormValues('platformOwner', derivedOwner)
	// 			}
	// 		}
	// 	}

	// 	fetchCoreIdentityUnconditionally()
	// }, [
	// 	isNonGke,
	// 	hasExistingPlatform,
	// 	formValues.gcpProjectID,
	// 	formValues.platformName,
	// 	platforms,
	// 	selectedProject,
	// 	selectedProjectID,
	// 	setValue,
	// 	setFormValues,
	// 	setTagFormValues
	// ])

	const handleSubmit = async () => {
		const isFormValid = await trigger() // Validates main form
		const inheritedTagsActive = Boolean(formValues.inheritGcpTagsBool)
		setSubmitErrorMessage('')

		// --- MANUALLY VALIDATE TAGS BEFORE SUBMITTING ---
		const getTagVal = (tag: any) => tag?.value || tag || ''
		const groupVal = getTagVal(tagFormValues.group)
		const fundingVal =
			getTagVal(tagFormValues.fundingSourceType)?.toLowerCase() || ''
		const dataClassVal = getTagVal(tagFormValues.dataClassification)

		let isWbsValid = true
		let isCostCenterValid = true

		if (fundingVal === 'opex') {
			isCostCenterValid = !!tagFormValues.costCenter?.trim()
		} else if (fundingVal === 'capex') {
			isWbsValid = !!tagFormValues.WBSCode?.trim()
		} else {
			// If funding is empty, everything is invalid
			isWbsValid = false
			isCostCenterValid = false
		}
		const isTagsValid =
			!!groupVal &&
			!!fundingVal &&
			!!dataClassVal &&
			isWbsValid &&
			isCostCenterValid

		// Block create-new-tags submissions if EITHER form is incomplete.
		if (!isFormValid || (!inheritedTagsActive && !isTagsValid)) {
			return
		}

		const callbacks = {
			onSuccess: () => {
				navigate('/applications')
				clearFormValues()
				clearTagFormValues()
				window.location.reload()
			},
			onError: (e: any) => {
				console.error(e)
				setSubmitErrorMessage(
					'Application submission failed. Please try again or contact support.'
				)
			}
		}
		const preparation = prepareApplicationSubmitPayloads({
			requestorID: thisUserEmail,
			formValues,
			tagFormValues,
			inheritedTagsActive,
			generatedTagsID: uuidv4(),
			creationTime: new Date().toISOString()
		})

		if (preparation.status === 'failed') {
			setSubmitErrorMessage(preparation.errorMessage)
			return
		}

		submitApplicationPayloads({
			...preparation.payloads,
			createTags,
			createNewApplication,
			callbacks
		})
	}

	const handleCancel = () => {
		clearFormValues()
		clearTagFormValues()
		setSubmitErrorMessage('')
		setCurrentStep(0)
		navigate(-1)
	}

	const getRequestTargetFields = () => {
		return getApplicationSubmitTargetFields()
	}

	const getApplicationDetailsFields = () => {
		const fields = [
			'applicationName',
			'applicationAlias',
			'applicationViewer',
			'applicationOwner',
			'applicationOwnerEmail'
		]

		if (isEphemeralActive) {
			fields.push('sourceEnvName', 'additionalEnvName')

			if (formValues.applicationName === 'cxs' && isSitEnvironmentChecked) {
				fields.push('sitEnvironmentName')
			}
		}

		return fields
	}

	const validateWizardStep = async (stepIndex = currentStep) => {
		if (stepIndex === 0) {
			const validateCurrentStep = stepValidators.current[stepIndex]
			if (validateCurrentStep) {
				return validateCurrentStep()
			}

			return trigger([
				...getRequestTargetFields(),
				...getApplicationDetailsFields()
			])
		}

		if (stepIndex === 1) {
			const validateCurrentStep = stepValidators.current[stepIndex]
			if (validateCurrentStep) {
				return validateCurrentStep()
			}

			return isTagsFormValid
		}

		return true
	}

	const handleWizardNavigate = async ({
		detail
	}: {
		detail: WizardProps.NavigateDetail
	}) => {
		if (
			detail.requestedStepIndex > currentStep &&
			!(await validateWizardStep(currentStep))
		) {
			return
		}

		setCurrentStep(detail.requestedStepIndex)
	}

	const handleWizardSubmit = async () => {
		if (submitValue) {
			return
		}

		if (!(await validateWizardStep(currentStep))) {
			return
		}

		try {
			await handleSubmit()
		} catch (error) {
			console.error(error)
			setSubmitErrorMessage(
				'Application submission failed before the request could be sent.'
			)
		}
	}

	const steps: Array<WizardProps.Step> = [
		{
			title: 'Application Details',
			content: (
				<ApplicationDetails
					registerValidation={(validate) => registerStepValidation(0, validate)}
					applications={applications}
					gcpProjects={gcpProjectsList}
					platforms={platforms}
					isApplicationsLoading={isApplicationsLoading}
					isApplicationsError={isApplicationsError}
					isGcpProjectsLoading={isGcpProjectsLoading}
					isPlatformsLoading={isPlatformsLoading}
				/>
			)
		},
		{
			title: 'Resource Tags',
			content: (
				<ApplicationTags
					registerValidation={(validate) => registerStepValidation(1, validate)}
				/>
			)
		},
		{
			title: 'Review',
			content: <ApplicationReview />
		}
	]

	return (
		<div>
			{showForm ? (
				<SpaceBetween direction="vertical" size="m">
					{submitErrorMessage ? (
						<Alert type="error" header="Submission failed">
							{submitErrorMessage}
						</Alert>
					) : null}
					<Wizard
						i18nStrings={i18nStrings}
						steps={steps}
						activeStepIndex={currentStep}
						onNavigate={handleWizardNavigate}
						onSubmit={handleWizardSubmit}
						onCancel={handleCancel}
						isLoadingNextStep={isLoading || isCreateTagsLoading || submitValue}
					/>
				</SpaceBetween>
			) : (
				<GkeOrNonGke
					onOnboardPlatform={handleYesClick}
					onContinue={handleNoClick}
					isLoading={isLoading}
				/>
			)}
		</div>
	)
}

export default NewApplicationForm
